import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle2, FileSignature, Loader2, User, Users, Receipt, Phone } from "lucide-react";
import { BrandMark } from "@/lib/branding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyParty = () => ({
  name: "", address: "", postal_code: "", locality: "",
  phone: "", email: "", nif: "", id_document: "",
});

const CARING_HEADER = {
  legal_name: "CARING POINT - APOIO DOMICILIÁRIO, UNIPESSOAL, LDA.",
  nif: "516 656 122",
  address: "Rua de Luís Veiga Leitão, n.º 116, Loja n.º 224",
  postal_code: "4050-339",
  locality: "Porto",
};

// ---------- Reusable field ----------
function Field({ label, value, onChange, required, placeholder, type = "text", testid, colSpan = "" }) {
  return (
    <div className={colSpan}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1.5">
        {label}{required && <span className="text-[hsl(var(--warning))] ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors"
      />
    </div>
  );
}

function PartyForm({ party, onChange, prefix, hidePhoneEmail = false }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Nome completo" required value={party.name}
        onChange={(v) => onChange({ ...party, name: v })}
        testid={`${prefix}-name`} colSpan="md:col-span-2" />
      <Field label="Morada" required value={party.address}
        onChange={(v) => onChange({ ...party, address: v })}
        testid={`${prefix}-address`} colSpan="md:col-span-2" />
      <Field label="Código Postal" required value={party.postal_code}
        onChange={(v) => onChange({ ...party, postal_code: v })}
        placeholder="0000-000" testid={`${prefix}-postal-code`} />
      <Field label="Localidade" required value={party.locality}
        onChange={(v) => onChange({ ...party, locality: v })}
        testid={`${prefix}-locality`} />
      {!hidePhoneEmail && (
        <>
          <Field label="Telefone" value={party.phone}
            onChange={(v) => onChange({ ...party, phone: v })}
            testid={`${prefix}-phone`} />
          <Field label="E-mail" type="email" value={party.email}
            onChange={(v) => onChange({ ...party, email: v })}
            testid={`${prefix}-email`} />
        </>
      )}
      <Field label="NIF" required value={party.nif}
        onChange={(v) => onChange({ ...party, nif: v })}
        placeholder="000000000" testid={`${prefix}-nif`} />
      <Field label="BI / CC / Passaporte" required value={party.id_document}
        onChange={(v) => onChange({ ...party, id_document: v })}
        testid={`${prefix}-id`} />
    </div>
  );
}

// ---------- Main page ----------
export default function PublicContractData() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [rgpdAccepted, setRgpdAccepted] = useState(false);

  const [beneficiaries, setBeneficiaries] = useState([emptyParty()]);
  const [family, setFamily] = useState(emptyParty());
  const [secondary, setSecondary] = useState({ name: "", relationship: "", phone: "", email: "" });
  const [billing, setBilling] = useState({ target: "beneficiary", beneficiary_index: 0, other_party: null });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/proposal/${token}/contract-data`)
      .then((r) => {
        setState(r.data);
        const cd = r.data.contract_data || {};
        const bens = (cd.beneficiaries && cd.beneficiaries.length) ? cd.beneficiaries : [emptyParty()];
        // Ensure we render exactly people_count entries
        const pc = r.data.people_count >= 2 ? 2 : 1;
        while (bens.length < pc) bens.push(emptyParty());
        setBeneficiaries(bens.slice(0, pc));
        setFamily(cd.family_responsible || emptyParty());
        setSecondary(cd.secondary_contact || { name: "", relationship: "", phone: "", email: "" });
        setBilling(cd.billing || { target: "beneficiary", beneficiary_index: 0, other_party: null });
        // If the client had submitted before, allow editing/confirming rather
        // than showing the "obrigado" wall. Every submission requires a fresh
        // RGPD tick — keep the checkbox unchecked on load.
        if (r.data.already_completed) {
          setAlreadyCompleted(true);
          setRgpdAccepted(false);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Não foi possível carregar o formulário."));
  }, [token]);

  const setBenAt = (i, v) => {
    const next = [...beneficiaries];
    next[i] = v;
    setBeneficiaries(next);
  };

  const submit = async () => {
    // Client validation
    for (let i = 0; i < beneficiaries.length; i++) {
      const b = beneficiaries[i];
      if (!b.name.trim() || !b.address.trim() || !b.nif.trim() || !b.id_document.trim()) {
        toast.error(`Cliente ${i + 1}: preencha nome, morada, NIF e BI/CC.`);
        return;
      }
    }
    if (!family.name.trim() || !family.nif.trim() || !family.id_document.trim()) {
      toast.error("Responsável familiar: preencha nome, NIF e BI/CC.");
      return;
    }
    if (billing.target === "other" && (!billing.other_party || !billing.other_party.name?.trim())) {
      toast.error("Faturação 'Outro': preencha os dados.");
      return;
    }
    if (!rgpdAccepted) {
      toast.error("Aceite a política de tratamento de dados (RGPD) para submeter.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/proposal/${token}/contract-data`, {
        beneficiaries,
        family_responsible: family,
        secondary_contact: secondary,
        billing: {
          target: billing.target,
          beneficiary_index: billing.target === "beneficiary" ? (billing.beneficiary_index || 0) : 0,
          other_party: billing.target === "other" ? (billing.other_party || emptyParty()) : null,
        },
        rgpd_accepted: true,
      });
      toast.success(alreadyCompleted ? "Dados actualizados. Obrigado!" : "Dados de contrato submetidos. Obrigado!");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao submeter.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-[hsl(var(--warning))] mb-4"><BrandMark /></div>
          <h1 className="font-display text-3xl mb-2">Formulário indisponível</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--warning))]" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background text-foreground" data-testid="contract-data-submitted">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-24 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="font-display text-4xl lg:text-5xl mb-4">Obrigado, {state.customer_name}.</h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Recebemos os seus dados. A sua Gestora vai preparar o contrato e enviar-lho para assinatura eletrónica nas próximas 24-48 horas úteis.
          </p>
          <div className="mt-10">
            <Link
              to={`/proposal/${token}`}
              className="inline-flex items-center gap-2 text-[hsl(var(--warning))] hover:text-[#F0D6AD] transition-colors"
              data-testid="back-to-proposal-link"
            >
              ← Voltar à proposta
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="contract-data-page">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between">
          <BrandMark />
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Dados de contrato</div>
        </div>
      </header>

      {/* Payment reference banner — visible while filling contract data so
          the client keeps the transfer details handy. Same info as the
          post-acceptance banner in PublicProposal. */}
      {state?.payment?.reference && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6" data-testid="contract-payment-banner">
          <div className="bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 rounded-2xl p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--success))] font-semibold mb-2">Dados para pagamento</div>
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <div className="text-[hsl(var(--success))] font-semibold">Referência</div>
              <div className="font-mono font-bold text-[hsl(var(--success))] break-all" data-testid="contract-payment-reference">{state.payment.reference}</div>
              <div className="text-[hsl(var(--success))] font-semibold">IBAN</div>
              <div className="font-mono text-foreground break-all">{state.payment.iban}</div>
              <div className="text-[hsl(var(--success))] font-semibold">SWIFT</div>
              <div className="font-mono text-foreground">{state.payment.swift}</div>
              <div className="text-[hsl(var(--success))] font-semibold">Titular</div>
              <div className="text-foreground">{state.payment.holder}</div>
            </div>
          </div>
        </section>
      )}

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-3">Passo final</div>
        <h1 className="font-display text-4xl lg:text-5xl mb-4 leading-tight inline-flex items-center gap-3">
          <FileSignature className="w-10 h-10 text-[hsl(var(--warning))]" /> Vamos preparar o seu contrato
        </h1>
        <p className="text-muted-foreground leading-relaxed max-w-2xl">
          Aceitou a proposta — só falta este último passo para formalizarmos o contrato. Confirme os dados abaixo (poderá corrigir o que já tínhamos) e nós tratamos do resto.
        </p>
        {alreadyCompleted && (
          <div data-testid="contract-confirm-banner"
               className="mt-6 rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-5 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-[hsl(var(--warning))]" />
            </div>
            <div className="text-sm text-[hsl(var(--warning))] leading-relaxed">
              <div className="font-semibold text-base mb-1">Já tínhamos estes dados registados</div>
              <div>
                {state?.last_edited_at && (
                  <>Última actualização: <b>{new Date(state.last_edited_at).toLocaleString("pt-PT")}</b>.<br /></>
                )}
                Por favor <b>confirme os dados anteriormente inseridos</b> ou edite-os se algo mudou entretanto. É obrigatório voltar a aceitar a política RGPD antes de submeter.
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 1º Outorgante — Caring (read-only) */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="caring-party-block">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-2">1.º Outorgante</div>
          <div className="font-ui text-xl mb-3">{CARING_HEADER.legal_name}</div>
          <div className="text-sm text-muted-foreground leading-relaxed space-y-0.5">
            <div>Contribuinte n.º {CARING_HEADER.nif}</div>
            <div>Sede: {CARING_HEADER.address}</div>
            <div>{CARING_HEADER.postal_code} {CARING_HEADER.locality}</div>
          </div>
        </div>
      </section>

      {/* Beneficiaries */}
      {beneficiaries.map((b, i) => (
        <section key={i} className="max-w-4xl mx-auto px-4 sm:px-6 pb-8" data-testid={`beneficiary-block-${i}`}>
          <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 lg:p-8">
            <div className="flex items-center gap-2 mb-6">
              <User className="w-5 h-5 text-[hsl(var(--warning))]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold">
                  2.º Outorgante
                </div>
                <div className="font-ui text-xl">
                  Cliente do Serviço {beneficiaries.length > 1 ? `#${i + 1}` : ""}
                </div>
              </div>
            </div>
            <PartyForm party={b} onChange={(v) => setBenAt(i, v)} prefix={`beneficiary-${i}`} />
          </div>
        </section>
      ))}

      {/* Family responsible */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-8" data-testid="family-block">
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-6">
            <Users className="w-5 h-5 text-[hsl(var(--warning))]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold">
                2.º Outorgante
              </div>
              <div className="font-ui text-xl">Responsável Familiar</div>
            </div>
          </div>
          <PartyForm party={family} onChange={setFamily} prefix="family" />
        </div>
      </section>

      {/* Secondary contact */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-8" data-testid="secondary-block">
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-6">
            <Phone className="w-5 h-5 text-[hsl(var(--warning))]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold">Contacto secundário</div>
              <div className="font-ui text-xl">Em caso de emergência (opcional)</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome" value={secondary.name}
              onChange={(v) => setSecondary({ ...secondary, name: v })}
              testid="secondary-name" />
            <Field label="Parentesco" value={secondary.relationship}
              onChange={(v) => setSecondary({ ...secondary, relationship: v })}
              placeholder="Ex: Filho, Cônjuge, Vizinho"
              testid="secondary-relationship" />
            <Field label="Telefone" value={secondary.phone}
              onChange={(v) => setSecondary({ ...secondary, phone: v })}
              testid="secondary-phone" />
            <Field label="E-mail" type="email" value={secondary.email}
              onChange={(v) => setSecondary({ ...secondary, email: v })}
              testid="secondary-email" />
          </div>
        </div>
      </section>

      {/* Billing */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-8" data-testid="billing-block">
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-6">
            <Receipt className="w-5 h-5 text-[hsl(var(--warning))]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold">Faturação</div>
              <div className="font-ui text-xl">Emissão da fatura em nome de:</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {[
              { key: "beneficiary", label: "Beneficiário" },
              { key: "family", label: "Responsável Familiar" },
              { key: "other", label: "Outro" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setBilling({ ...billing, target: opt.key, other_party: opt.key === "other" ? (billing.other_party || emptyParty()) : null })}
                data-testid={`billing-target-${opt.key}`}
                className={`px-5 py-4 rounded-2xl border transition-colors text-left ${
                  billing.target === opt.key
                    ? "bg-[hsl(var(--warning))]/15 border-[hsl(var(--warning))] text-primary font-semibold"
                    : "bg-background border-border text-muted-foreground hover:border-white/30 hover:text-primary"
                }`}
              >
                <div className={`text-[10px] uppercase tracking-[0.2em] mb-1 ${billing.target === opt.key ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`}>Opção</div>
                <div className="font-ui">{opt.label}</div>
              </button>
            ))}
          </div>

          {billing.target === "beneficiary" && beneficiaries.length > 1 && (
            <div className="mb-4" data-testid="billing-beneficiary-picker">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Qual beneficiário fatura?</label>
              <div className="flex gap-2">
                {beneficiaries.map((b, i) => (
                  <button
                    key={i}
                    onClick={() => setBilling({ ...billing, beneficiary_index: i })}
                    className={`px-4 py-2 rounded-full border text-sm ${
                      (billing.beneficiary_index || 0) === i
                        ? "bg-[hsl(var(--warning))]/20 border-[hsl(var(--warning))] text-primary font-semibold"
                        : "bg-background border-border text-muted-foreground"
                    }`}
                    data-testid={`billing-beneficiary-idx-${i}`}
                  >
                    {b.name || `Beneficiário #${i + 1}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {billing.target === "other" && (
            <div className="mt-2" data-testid="billing-other-form">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">Dados do faturado</div>
              <PartyForm
                party={billing.other_party || emptyParty()}
                onChange={(v) => setBilling({ ...billing, other_party: v })}
                prefix="billing-other"
                hidePhoneEmail={false}
              />
            </div>
          )}
        </div>
      </section>

      {/* RGPD consent + submit */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-4">
        {state?.rgpd_notice?.text && (
          <div className="bg-card border border-border rounded-2xl p-4 sm:p-6" data-testid="rgpd-consent-block">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-2">Política de tratamento de dados (RGPD)</div>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
              {state.rgpd_notice.text}
            </p>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="rgpd-consent-label">
              <input
                type="checkbox"
                checked={rgpdAccepted}
                onChange={(e) => setRgpdAccepted(e.target.checked)}
                data-testid="rgpd-consent-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#F59E0B] cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-foreground leading-relaxed">
                <b>Li e aceito</b> a política de tratamento de dados descrita acima
                {state.rgpd_notice.version && (
                  <span className="text-muted-foreground text-xs"> (v{state.rgpd_notice.version})</span>
                )}.
              </span>
            </label>
          </div>
        )}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-card border border-border rounded-2xl p-6">
          <div>
            <div className="font-ui text-lg mb-1">
              {alreadyCompleted ? "Confirme ou actualize os dados" : "Pronto para submeter?"}
            </div>
            <div className="text-sm text-muted-foreground">
              {alreadyCompleted
                ? "A sua Gestora revê as alterações e prepara o contrato actualizado."
                : "A sua Gestora recebe os dados imediatamente e prepara o contrato."}
            </div>
          </div>
          <button
            onClick={submit}
            disabled={submitting || !rgpdAccepted}
            className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-foreground font-semibold rounded-full px-8 py-3.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="contract-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
            {alreadyCompleted ? "Confirmar dados" : "Submeter dados"}
          </button>
        </div>
      </section>
    </div>
  );
}
